import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as LinkIcon, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import {
  createLink,
  deleteLink,
  getArtifact,
  listLinks,
  type Artifact,
  type ArtifactLink,
  type ArtifactLinkType,
  type CreateArtifactLinkBody,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/button';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { LinkedArtifactPicker } from './LinkedArtifactPicker';

interface ArtifactLinksPanelProps {
  artifact: Artifact;
}

/**
 * `listLinks` returns links where the current artifact may be either
 * endpoint. Directional link types (`references`, `derived_from`,
 * `implements`, `supersedes`) need flipped labels when this artifact is the
 * `to` side. `related_to` is symmetric: one row is stored and queried both
 * directions, so both sides use the same label.
 */
const LINK_TYPE_LABEL: Record<ArtifactLinkType, { outgoing: string; incoming: string }> = {
  references: { outgoing: 'references', incoming: 'referenced by' },
  derived_from: { outgoing: 'derived from', incoming: 'source of' },
  implements: { outgoing: 'implements', incoming: 'implemented by' },
  supersedes: { outgoing: 'supersedes', incoming: 'superseded by' },
  related_to: { outgoing: 'related to', incoming: 'related to' },
};

export function ArtifactLinksPanel({ artifact }: ArtifactLinksPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useSession();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);

  const linksQuery = useQuery<ArtifactLink[]>({
    queryKey: ['artifact', artifact.id, 'links'],
    queryFn: () => listLinks(artifact.id),
  });

  const links = linksQuery.data ?? [];

  // Resolve the remote id per row: the current artifact may be either endpoint.
  // Key the fetch to ['artifact', remoteId] so the cache is shared with detail pages.
  const remoteIds = links.map(link =>
    link.fromArtifactId === artifact.id ? link.toArtifactId : link.fromArtifactId
  );
  const remoteQueries = useQueries({
    queries: remoteIds.map(remoteId => ({
      queryKey: ['artifact', remoteId] as const,
      queryFn: (): Promise<Artifact> => getArtifact(remoteId),
      enabled: remoteId.length > 0,
      staleTime: 30_000,
    })),
  });

  const [deleteTarget, setDeleteTarget] = useState<ArtifactLink | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLinkType, setPickerLinkType] = useState<ArtifactLinkType>('references');
  const [createError, setCreateError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => deleteLink(linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'links'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setDeleteTarget(null);
    },
    onError: err => {
      setDeleteError(err.message);
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateArtifactLinkBody) => createLink(artifact.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'links'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setPickerOpen(false);
      setCreateError(null);
    },
    onError: err => {
      setCreateError(err.message);
    },
  });

  if (linksQuery.isLoading) {
    return <div className="text-xs text-text-secondary">Loading links…</div>;
  }

  if (linksQuery.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {linksQuery.error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      {links.length === 0 ? (
        <span className="text-xs text-text-secondary">No links yet.</span>
      ) : (
        <ul className="flex flex-col gap-2">
          {links.map((link, idx) => {
            const remote = remoteQueries[idx];
            const remoteArtifact = remote.data;
            const isOutgoing = link.fromArtifactId === artifact.id;
            const remoteId = isOutgoing ? link.toArtifactId : link.fromArtifactId;
            const label = isOutgoing
              ? LINK_TYPE_LABEL[link.linkType].outgoing
              : LINK_TYPE_LABEL[link.linkType].incoming;
            const isProtectedSupersedes =
              link.linkType === 'supersedes' &&
              artifact.type === 'adr' &&
              remoteArtifact?.type === 'adr';

            return (
              <li
                key={link.id}
                className="flex items-center gap-2 rounded-md border border-border bg-surface/40 p-2"
              >
                <LinkIcon className="size-4 shrink-0 text-text-secondary" />
                <span className="rounded-md bg-surface-elevated px-2 py-0.5 font-mono text-xs text-text-secondary">
                  {label}
                </span>
                <div className="flex flex-1 flex-col">
                  {remote.isLoading && (
                    <span className="text-xs text-text-secondary">Loading target…</span>
                  )}
                  {remote.isError && (
                    <span className="font-mono text-xs text-text-secondary">
                      {remoteId} (cannot read)
                    </span>
                  )}
                  {remoteArtifact && (
                    <Link
                      to={`/artifacts/${encodeURIComponent(remoteArtifact.id)}`}
                      className="text-primary hover:underline"
                    >
                      {remoteArtifact.title}
                    </Link>
                  )}
                  {remoteArtifact && (
                    <span className="font-mono text-xs text-text-secondary">
                      {remoteArtifact.type} / {remoteArtifact.status}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!isOwnerOrAdmin || deleteMutation.isPending || isProtectedSupersedes}
                  title={
                    isProtectedSupersedes
                      ? "ADR supersedes links cannot be deleted directly. The link and the older ADR's superseded status are an indivisible pair."
                      : undefined
                  }
                  onClick={(): void => {
                    setDeleteError(null);
                    setDeleteTarget(link);
                  }}
                  className="inline-flex items-center gap-1.5"
                >
                  <Trash2 className="size-4" />
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
        <select
          value={pickerLinkType}
          onChange={event => {
            setPickerLinkType(event.target.value as ArtifactLinkType);
          }}
          className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
          aria-label="Link type"
          disabled={!isOwnerOrAdmin}
        >
          <option value="references">references</option>
          <option value="derived_from">derived from</option>
          <option value="implements">implements</option>
          <option value="related_to">related to</option>
        </select>
        <Button
          size="sm"
          variant="outline"
          disabled={!isOwnerOrAdmin}
          onClick={(): void => {
            setCreateError(null);
            setPickerOpen(true);
          }}
          className="inline-flex items-center gap-1.5"
        >
          <Plus className="size-4" />
          Add link
        </Button>
        <span className="text-xs text-text-secondary">
          Supersedes is created via the lifecycle panel's "Supersede an older ADR" action.
        </span>
      </div>
      <LinkedArtifactPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        codebaseId={artifact.codebaseId}
        excludeArtifactId={artifact.id}
        title="Pick the target artifact"
        description={`The new link will be of type "${LINK_TYPE_LABEL[pickerLinkType].outgoing}".`}
        confirmText="Create link"
        onPick={(targetId): void => {
          setCreateError(null);
          createMutation.mutate({ toArtifactId: targetId, linkType: pickerLinkType });
        }}
        pending={createMutation.isPending}
        errorMessage={createError}
      />
      <ConfirmActionDialog
        open={deleteTarget !== null}
        onOpenChange={next => {
          if (!next) setDeleteTarget(null);
        }}
        title="Remove link?"
        description={
          deleteTarget
            ? ((): string => {
                const isOutgoing = deleteTarget.fromArtifactId === artifact.id;
                const remoteId = isOutgoing
                  ? deleteTarget.toArtifactId
                  : deleteTarget.fromArtifactId;
                const label = isOutgoing
                  ? LINK_TYPE_LABEL[deleteTarget.linkType].outgoing
                  : LINK_TYPE_LABEL[deleteTarget.linkType].incoming;
                return `This removes the "${label}" link involving artifact ${remoteId}. The other artifact is not deleted.`;
              })()
            : ''
        }
        confirmText="Remove link"
        confirmTone="danger"
        onConfirm={(): void => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        pending={deleteMutation.isPending}
        errorMessage={deleteError}
      />
    </div>
  );
}
